import React, { useState, useEffect } from 'react';
import { Builder, BuilderAddress, BuilderPhone } from '../types/builder';
import ImageUpload from './ImageUpload';
import { uploadImage } from '../services/uploadService';
import { getImageUrl } from '../services/imageService';
import api from '../services/api';
import '../styles/DrawerForm.css';
import '../styles/Buttons.css';

interface BuilderFormSliderProps {
  workspaceId: string;
  builderId?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

interface BuilderFormData {
  name: string;
  description: string;
  logoUrl: string;
  logoFile: File | null;
  logoId: string;
  deleteLogo: boolean; // Track if existing logo should be deleted
  contactName: string;
  contactEmail: string;
  contactPhoneNumber: string;
  contactPhoneFormatted: string;
  contactWebsite: string;
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

const BuilderFormSlider: React.FC<BuilderFormSliderProps> = ({
  workspaceId,
  builderId,
  onSuccess,
  onCancel
}) => {
  const isEditMode = !!builderId;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<BuilderFormData>({
    name: '',
    description: '',
    logoUrl: '',
    logoFile: null,
    logoId: '',
    deleteLogo: false,
    contactName: '',
    contactEmail: '',
    contactPhoneNumber: '',
    contactPhoneFormatted: '',
    contactWebsite: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
    country: 'Canada'
  });

  useEffect(() => {
    if (isEditMode && builderId) {
      fetchBuilder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builderId, isEditMode]);

  const fetchBuilder = async () => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      const response = await api.get(
        `/workspaces/${workspaceId}/builders/${builderId}`
      );

      const builder: Builder = response.data.builder;

      // Fetch presigned URL if logoId exists
      let logoUrl = '';
      if (builder.logoId) {
        try {
          logoUrl = await getImageUrl(workspaceId, builder.logoId);
        } catch (err) {
          console.error('Error fetching logo URL:', err);
        }
      }

      setFormData({
        name: builder.name || '',
        description: builder.description || '',
        logoUrl: logoUrl,
        logoFile: null,
        logoId: builder.logoId || '',
        deleteLogo: false,
        contactName: builder.contactName || '',
        contactEmail: builder.contactEmail || '',
        contactPhoneNumber: builder.contactPhone?.number || '',
        contactPhoneFormatted: builder.contactPhone?.formatted || '',
        contactWebsite: builder.contactWebsite || '',
        street1: builder.address?.street1 || '',
        street2: builder.address?.street2 || '',
        city: builder.address?.city || '',
        state: builder.address?.state || '',
        zip: builder.address?.zip || '',
        country: builder.address?.country || 'Canada'
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch builder');
      console.error('Error fetching builder:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phone: string): string => {
    const cleaned = phone.replace(/\D/g, '');

    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 11 && cleaned[0] === '1') {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }

    return phone;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Auto-format phone number
    if (name === 'contactPhoneNumber') {
      const formatted = formatPhoneNumber(value);
      setFormData((prev) => ({ ...prev, contactPhoneFormatted: formatted }));
    }
  };

  const handleLogoChange = (file: File | null, previewUrl: string | null) => {
    if (file === null && previewUrl === null) {
      // User deleted the logo
      setFormData((prev) => ({
        ...prev,
        logoFile: null,
        logoUrl: '',
        deleteLogo: prev.logoId ? true : false // Only mark for deletion if there was an existing logo
      }));
    } else {
      // User selected a new logo
      setFormData((prev) => ({
        ...prev,
        logoFile: file,
        logoUrl: previewUrl || prev.logoUrl,
        deleteLogo: false
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    console.log('Form submitted!', { formData });

    // Validation
    if (!formData.name.trim()) {
      setError('Builder name is required');
      return;
    }

    try {
      setLoading(true);

      let logoId = formData.logoId;

      // Handle logo deletion
      if (formData.deleteLogo && formData.logoId) {
        console.log('Deleting logo...', formData.logoId);
        try {
          await api.delete(
            `/workspaces/${workspaceId}/upload/image/${formData.logoId}`
          );
          logoId = ''; // Clear the logoId
        } catch (err) {
          console.error('Error deleting logo:', err);
          // Continue with submission even if delete fails
        }
      }

      // Upload logo if a new file was selected
      if (formData.logoFile) {
        console.log('Uploading logo...', formData.logoFile);
        const uploadedImage = await uploadImage({
          workspaceId,
          file: formData.logoFile,
          entityType: 'builder',
          entityId: builderId || workspaceId,
          imageType: 'logo'
        });
        console.log('Logo uploaded:', uploadedImage);
        logoId = uploadedImage._id;
      }

      const hasAddress = formData.street1.trim() || formData.street2.trim() ||
                        formData.city.trim() || formData.state.trim() ||
                        formData.zip.trim() || formData.country.trim();

      const address: BuilderAddress | undefined = hasAddress ? {
        street1: formData.street1.trim(),
        street2: formData.street2.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        zip: formData.zip.trim(),
        country: formData.country.trim()
      } : undefined;

      const contactPhone: BuilderPhone | undefined = formData.contactPhoneNumber.trim()
        ? {
            number: formData.contactPhoneNumber.trim(),
            formatted: formData.contactPhoneFormatted.trim()
          }
        : undefined;

      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        logoId: logoId || undefined,
        contactName: formData.contactName.trim() || undefined,
        contactEmail: formData.contactEmail.trim() || undefined,
        contactPhone,
        contactWebsite: formData.contactWebsite.trim() || undefined,
        address,
        ...(formData.deleteLogo && { logoId: null }) // Explicitly set logoId to null if deleting
      };

      if (isEditMode) {
        await api.put(
          `/workspaces/${workspaceId}/builders/${builderId}`,
          payload
        );
      } else {
        await api.post(
          `/workspaces/${workspaceId}/builders`,
          payload
        );
      }

      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} builder`);
      console.error('Error submitting form:', err);
    } finally {
      setLoading(false);
    }
  };

  // Show loading spinner while fetching builder data for edit mode
  if (isEditMode && loading && !formData.name) {
    return (
      <div className="builders-loading" style={{ padding: '64px 0' }}>
        <div className="builders-loading-spinner" />
      </div>
    );
  }

  return (
    <div className="drawer-form">
      <form onSubmit={handleSubmit} id="builder-form">
        {error && (
          <div className="form-error" style={{ marginBottom: '24px' }}>
            {error}
          </div>
        )}

        {/* Basic Information */}
        <h3>Basic Information</h3>

        <div className="form-row" style={{ alignItems: 'flex-start', gap: '24px' }}>
          {/* Left Column - Name and Description */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div className="form-group">
              <label htmlFor="name" className="form-label">
                Builder Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                className="form-input"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Acme Developments Inc."
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="description" className="form-label">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                className="form-input"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Brief description of the builder"
                rows={3}
                disabled={loading}
                style={{ resize: 'vertical' }}
              />
            </div>
          </div>

          {/* Right Column - Logo */}
          <div style={{ flex: 1 }}>
            <ImageUpload
              label="Logo"
              value={formData.logoUrl}
              onChange={handleLogoChange}
              disabled={loading}
              placeholder="Click to upload, drag & drop, or paste logo"
            />
          </div>
        </div>

        {/* Contact Information */}
        <h3>Contact Information</h3>

        <div className="form-group">
          <label htmlFor="contactName" className="form-label">
            Contact Name
          </label>
          <input
            type="text"
            id="contactName"
            name="contactName"
            className="form-input"
            value={formData.contactName}
            onChange={handleInputChange}
            placeholder="John Doe"
            disabled={loading}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="contactEmail" className="form-label">
              Contact Email
            </label>
            <input
              type="email"
              id="contactEmail"
              name="contactEmail"
              className="form-input"
              value={formData.contactEmail}
              onChange={handleInputChange}
              placeholder="contact@example.com"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="contactPhoneNumber" className="form-label">
              Contact Phone
            </label>
            <input
              type="tel"
              id="contactPhoneNumber"
              name="contactPhoneNumber"
              className="form-input"
              value={formData.contactPhoneNumber}
              onChange={handleInputChange}
              placeholder="+1 (555) 123-4567"
              disabled={loading}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="contactWebsite" className="form-label">
            Website
          </label>
          <input
            type="url"
            id="contactWebsite"
            name="contactWebsite"
            className="form-input"
            value={formData.contactWebsite}
            onChange={handleInputChange}
            placeholder="https://www.example.com"
            disabled={loading}
          />
        </div>

        {/* Address */}
        <h3>Address</h3>

        <div className="form-group">
          <label htmlFor="street1" className="form-label">
            Street Address 1
          </label>
          <input
            type="text"
            id="street1"
            name="street1"
            className="form-input"
            value={formData.street1}
            onChange={handleInputChange}
            placeholder="123 Main St"
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="street2" className="form-label">
            Street Address 2
          </label>
          <input
            type="text"
            id="street2"
            name="street2"
            className="form-input"
            value={formData.street2}
            onChange={handleInputChange}
            placeholder="Suite 200"
            disabled={loading}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="city" className="form-label">
              City
            </label>
            <input
              type="text"
              id="city"
              name="city"
              className="form-input"
              value={formData.city}
              onChange={handleInputChange}
              placeholder="Anytown"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="state" className="form-label">
              State/Province
            </label>
            <input
              type="text"
              id="state"
              name="state"
              className="form-input"
              value={formData.state}
              onChange={handleInputChange}
              placeholder="State"
              disabled={loading}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="zip" className="form-label">
              Postal/Zip Code
            </label>
            <input
              type="text"
              id="zip"
              name="zip"
              className="form-input"
              value={formData.zip}
              onChange={handleInputChange}
              placeholder="12345"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="country" className="form-label">
              Country
            </label>
            <input
              type="text"
              id="country"
              name="country"
              className="form-input"
              value={formData.country}
              onChange={handleInputChange}
              placeholder="Canada"
              disabled={loading}
            />
          </div>
        </div>

      </form>
    </div>
  );
};

export default BuilderFormSlider;
